/**
 * Simplified usage tracking service.
 * Tracks daily usage via backend + DynamoDB.
 */

import { type UsageStatus } from '../config/usageLimits';
import { logger } from '../utils/logger';
import { APP_CONFIG } from '../config/generated';
import { getUserIdentityForApi } from '../utils/auth-aws-sdk';

class SimpleUsageTrackingService {
  /**
   * Get today's date string (YYYY-MM-DD)
   */
  private getTodayString(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  /**
   * Get current user's usage status
   */
  async getUsageStatus(): Promise<UsageStatus | null> {
    try {
      const idToken = localStorage.getItem('cognito_id_token');
      if (!idToken) {
        logger.warn('Unauthenticated user; cannot fetch usage status');
        return null;
      }

      const mode = (APP_CONFIG as any).mode || 'dev';
      const envConfig = (APP_CONFIG as any).ENV_CONFIGS?.[mode] || APP_CONFIG.ENV_CONFIGS.dev;
      const backendUrl = (envConfig?.API || 'http://localhost:5050').replace(/\/$/, '');
      const { getApiHeadersWithoutContentType } = await import('../utils/apiHeaders');
      const url = `${backendUrl}/api/usage/status`;
      
      logger.log('📡 Fetching usage status from:', url);
      const resp = await fetch(url, {
        method: 'GET',
        headers: getApiHeadersWithoutContentType(),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        if (resp.status === 404) {
          logger.warn(`⚠️ Usage status endpoint not found (404): ${url}. This endpoint may not be implemented yet.`);
        } else {
          logger.error(`❌ Failed to fetch usage status (${resp.status}):`, data);
        }
        return null;
      }

      const status: UsageStatus = {
        remaining: data.remaining ?? 0,
        used: data.used ?? 0,
        limit: data.limit ?? 0,
        resetDate: this.getTodayString(),
        canUse: !!data.can_use,
        plan: data.plan ?? 'free',
        unlimited: !!data.unlimited,
      };

      logger.log('📊 Usage status (backend):', status);
      return status;
    } catch (error) {
      logger.error('Failed to fetch usage status:', error);
      return null;
    }
  }

  /**
   * Check whether an AI operation can be performed
   */
  async canPerformAIOperation(): Promise<boolean> {
    const status = await this.getUsageStatus();
    const canUse = status?.canUse || false;
    logger.log('🔍 Checking AI operation permission:', canUse);
    return canUse;
  }

  /**
   * Record one AI operation
   */
  async recordAIOperation(): Promise<boolean> {
    try {
      const idToken = localStorage.getItem('cognito_id_token');
      if (!idToken) {
        logger.warn('Unauthenticated user; cannot record AI operation');
        return false;
      }

      const mode = (APP_CONFIG as any).mode || 'dev';
      const envConfig = (APP_CONFIG as any).ENV_CONFIGS?.[mode] || APP_CONFIG.ENV_CONFIGS.dev;
      const backendUrl = (envConfig?.API || 'http://localhost:5050').replace(/\/$/, '');
      const { getApiHeaders } = await import('../utils/apiHeaders');
      const url = `${backendUrl}/api/usage/consume`;
      
      logger.log('📡 Recording AI operation to:', url);
      const resp = await fetch(url, {
        method: 'POST',
        headers: getApiHeaders(),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        if (resp.status === 404) {
          logger.warn(`⚠️ Usage consume endpoint not found (404): ${url}. This endpoint may not be implemented yet.`);
        } else {
          logger.warn('❌ Daily AI operation limit reached or record failed:', data);
        }
        return false;
      }

      logger.log('✅ AI operation recorded (backend):', data);
      return true;
    } catch (error) {
      logger.error('❌ Failed to record AI operation:', error);
      return false;
    }
  }

  /**
   * Reset usage (metadata is managed by backend)
   */
  resetUsage(): void {
    logger.log('🔄 Usage reset is managed by backend/DDB; no local reset required');
  }

  /**
   * Get usage limit metadata
   */
  getUsageLimits() {
    return {
      dailyLimit: 0,
      resetTime: '00:00:00'
    };
  }
}

// Singleton instance
export const simpleUsageTrackingService = new SimpleUsageTrackingService();

// Convenience exports
export const getUsageStatus = () => simpleUsageTrackingService.getUsageStatus();
export const canPerformAIOperation = () => simpleUsageTrackingService.canPerformAIOperation();
export const recordAIOperation = () => simpleUsageTrackingService.recordAIOperation();
export const resetUsage = () => simpleUsageTrackingService.resetUsage();
export const getUsageLimits = () => simpleUsageTrackingService.getUsageLimits();
